import React, { useState, useEffect } from "react";
import { CSVLink } from "react-csv";
import {
  Card,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiButton from "components/VuiButton";
import VuiTypography from "components/VuiTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import VuiPagination from "components/VuiPagination";
import Icon from "@mui/material/Icon";
import { useParams } from "react-router-dom";
import jsPDF from "jspdf";
import logoBase64 from "assets/images/logoBase64";
import "jspdf-autotable";

// Helper function to format date from database time field
function formatDateTimeFromDB(timeString) {
  if (!timeString) return { date: "Unknown Date", time: "Unknown Time" };

  try {
    const date = new Date(timeString);
    const formattedDate = `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1)
      .toString()
      .padStart(2, "0")}/${date.getFullYear()}`;
    const formattedTime = `${date.getHours().toString().padStart(2, "0")}:${date
      .getMinutes()
      .toString()
      .padStart(2, "0")}`;
    return {
      date: formattedDate,
      time: formattedTime,
      fullDateTime: timeString,
    };
  } catch (error) {
    console.error("Error parsing time string:", timeString, error);
    return { date: "Unknown Date", time: "Unknown Time" };
  }
}

// Material mapping function - matches backend
function getMaterialName(materialCode) {
  const materialMap = {
    1: "0/4mm (MP)",
    2: "0/4mm (Screed)",
    3: "0/20mm (Ballast)",
    4: "4/10mm (Gravel)",
    5: "4/20mm (Gravel)",
    6: "10/20mm (Gravel)",
    7: "20/40mm (Gravel)",
  };
  const code = String(materialCode || "");
  return materialMap[code] || "Unknown Material";
}

function formatCheckValue(value) {
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }
  try {
    if (String(value).trim() === "1" || String(value).trim().toLowerCase() === "true") {
      return "Yes";
    } else if (String(value).trim() === "0" || String(value).trim().toLowerCase() === "false") {
      return "No";
    } else {
      return "N/A";
    }
  } catch {
    return "N/A";
  }
}

function TrainLoadDataExport() {
  const { site_id } = useParams();
  const [trainLoads, setTrainLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: "",
    endDate: "",
  });
  const [siteName, setSiteName] = useState("");
  const [fullReportData, setFullReportData] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [managerReportDialogOpen, setManagerReportDialogOpen] = useState(false);
  const [customerReportDialogOpen, setCustomerReportDialogOpen] = useState(false);
  const [currentTrainLoad, setCurrentTrainLoad] = useState(null);
  const [managerReportData, setManagerReportData] = useState(null);
  const [customerReportData, setCustomerReportData] = useState(null);
  const [loadingButtons, setLoadingButtons] = useState({});
  const [companyLogo, setCompanyLogo] = useState(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [downloadingAllReports, setDownloadingAllReports] = useState(false);
  const [allReportsData, setAllReportsData] = useState([]);

  const columns = [
    { name: "Destination", align: "left" },
    { name: "Customer Order No", align: "center" },
    { name: "Brett Reference No", align: "center" },
    { name: "Actions", align: "center" },
  ];

  useEffect(() => {
    const fetchSiteData = async () => {
      try {
        const response = await fetch(
          `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/site/profile?site_id=${site_id}`
        );
        const data = await response.json();
        setSiteName(data.name);
        await fetchCompanyLogo(data.name);
      } catch (error) {
        console.error("Error fetching site data:", error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchSiteData();
  }, [site_id]);

  useEffect(() => {
    if (trainLoads.length > 0) {
      loadDetailedDataForCurrentPage();
    }
  }, [page, trainLoads]);

  const fetchCompanyLogo = async (siteName) => {
    try {
      const response = await fetch(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/company/logo/by-site?site_name=${encodeURIComponent(
          siteName
        )}`
      );
      const data = await response.json();
      if (data.logo_url) {
        const logoResponse = await fetch(data.logo_url);
        const blob = await logoResponse.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          setCompanyLogo(reader.result);
        };
        reader.readAsDataURL(blob);
      }
    } catch (error) {
      console.error("Error fetching company logo:", error);
    }
  };

  const fetchGatewayName = async () => {
    try {
      const response = await fetch(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/gateway/profile?site_id=${site_id}`
      );
      const data = await response.json();
      return data[0]?.name;
    } catch (error) {
      console.error("Error fetching gateway name:", error.message);
      return null;
    }
  };

  const fetchTrainLoads = async () => {
    try {
      setLoading(true);
      setPageLoading(true);
      const gatewayName = await fetchGatewayName();
      if (!gatewayName) {
        setLoading(false);
        setPageLoading(false);
        return;
      }

      const startDateFormatted = dateRange.startDate;
      const endDateFormatted = dateRange.endDate;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 25000);

      const response = await fetch(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/query-timestream?query_type=train_loads&thing_name=${gatewayName}&start_date=${startDateFormatted}&end_date=${endDateFormatted}`,
        {
          signal: controller.signal,
        }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Filter for complete train loads (Status = 1)
      const completeTrainLoads = data.filter((load) => {
        const status = load.Status;
        return status === 1 || status === "1" || status === true;
      });

      console.log(`Total loads: ${data.length}, Complete loads: ${completeTrainLoads.length}`);

      const formattedData = completeTrainLoads.map((load) => {
        const { date } = formatDateTimeFromDB(load.time);
        return {
          id: load.Train_Load_ID,
          date: date,
          siteFrom: siteName || "Unknown",
          destination: load.Destination || "Loading...",
          customerOrderNo: load.Customer_Order_No || "Loading...",
          brettReferenceNo: load.Brett_Reference_No || "Loading...",
          flowNo: load.Flow_No || "Loading...",
          status: load.Status,
          rawData: load,
          detailsLoaded: false,
        };
      });

      setTrainLoads(formattedData);
      setPageLoading(false);
    } catch (error) {
      if (error.name === "AbortError") {
        console.error("Request timed out");
        alert("The request timed out. Please try a smaller date range.");
      } else {
        console.error("Error fetching train loads:", error);
      }
      setPageLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const loadDetailedDataForCurrentPage = async () => {
    if (trainLoads.length === 0) {
      setPageLoading(false);
      return;
    }

    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const currentPageLoads = trainLoads.slice(startIndex, endIndex);

    if (currentPageLoads.length === 0) {
      setPageLoading(false);
      return;
    }

    const loadsToFetch = currentPageLoads.filter(
      (load) => !load.detailsLoaded && (load.status === 1 || load.status === "1")
    );

    if (loadsToFetch.length === 0) {
      setPageLoading(false);
      return;
    }

    const gatewayName = await fetchGatewayName();
    if (!gatewayName) {
      setPageLoading(false);
      return;
    }

    const updatedLoads = await Promise.all(
      loadsToFetch.map(async (load) => {
        try {
          const fullReportResponse = await fetch(
            `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/query-timestream?query_type=train_load_report&thing_name=${gatewayName}&train_load_id=${load.id}`
          );
          if (fullReportResponse.ok) {
            const fullReport = await fullReportResponse.json();
            const latestRecord = fullReport[fullReport.length - 1];
            return {
              ...load,
              destination: latestRecord?.Destination || load.destination,
              customerOrderNo: latestRecord?.Customer_Order_No || "N/A",
              brettReferenceNo: latestRecord?.Brett_Reference_No || "N/A",
              flowNo: latestRecord?.Flow_No || "N/A",
              detailsLoaded: true,
            };
          }
        } catch (error) {
          console.error(`Error fetching details for train load ${load.id}:`, error);
        }
        return {
          ...load,
          customerOrderNo: "N/A",
          brettReferenceNo: "N/A",
          flowNo: "N/A",
          detailsLoaded: true,
        };
      })
    );

    setTrainLoads((prev) => {
      const updated = [...prev];
      updatedLoads.forEach((updatedLoad) => {
        const index = updated.findIndex((load) => load.id === updatedLoad.id);
        if (index !== -1) {
          updated[index] = updatedLoad;
        }
      });
      return updated;
    });

    setPageLoading(false);
  };

  const fetchFullReport = async (trainLoadId) => {
    try {
      setReportLoading(true);
      const gatewayName = await fetchGatewayName();
      if (!gatewayName) return null;

      const response = await fetch(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/query-timestream?query_type=train_load_report&thing_name=${gatewayName}&train_load_id=${trainLoadId}`
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error("API Error:", errorData);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Full report received:", data);
      console.log("Is array?", Array.isArray(data));
      console.log("Number of records:", data?.length);
      
      if (data && Array.isArray(data) && data.length > 0) {
        console.log("First record keys:", Object.keys(data[0]));
        setFullReportData(data);
        return data;
      } else {
        console.error("Invalid or empty response:", data);
        return null;
      }
    } catch (error) {
      console.error("Error fetching full report:", error);
      return null;
    } finally {
      setReportLoading(false);
    }
  };

  const fetchManagerReport = async (trainLoadId) => {
    try {
      setReportLoading(true);
      const fullReport = await fetchFullReport(trainLoadId);
      
      if (!fullReport || !Array.isArray(fullReport) || fullReport.length === 0) {
        console.error("No valid report data received");
        alert("No data found for this train load. Please check if the train load ID is correct.");
        return null;
      }

      const latestRecord = fullReport[fullReport.length - 1];
      
      console.log("Latest record keys:", Object.keys(latestRecord));
      console.log("Latest record sample:", latestRecord);
      
      const noWagons = parseInt(latestRecord.No_Wagons) || 0;
      if (noWagons === 0) {
        console.warn("No wagons found in the report data");
      }
      
      const wagons = [];
      let totalWeight = 0;

      for (let i = 1; i <= noWagons; i++) {
        const wagonId = latestRecord[`Wagon_ID_${i}`];
        if (wagonId && wagonId !== "N/A" && wagonId !== "0") {
          const materialCode = latestRecord[`Wagon_${i}_Material`];
          const materialName = getMaterialName(materialCode);
          const targetTonnage = latestRecord[`Wagon_${i}_Target_Tonnage`];
          const loadedTonnage = latestRecord[`Wagon_${i}_Loaded_Tonnage`];
          const averageMoisture = latestRecord[`Wagon_${i}_Average_Moisture`];
          const comments = latestRecord[`Wagon_${i}_Comments`];
          
          console.log(`Wagon ${i}: ID=${wagonId}, Target=${targetTonnage}, Loaded=${loadedTonnage}, Moisture=${averageMoisture}`);
          
          wagons.push({
            wagon_no: i,
            wagon_id: wagonId,
            material: materialName,
            material_code: materialCode,
            target_tonnage: targetTonnage || "0",
            amount: loadedTonnage || "0",
            moisture: averageMoisture || "0",
            comments: comments || "",
          });
          
          totalWeight += parseFloat(loadedTonnage) || 0;
        }
      }

      const timeValue = latestRecord.time || latestRecord.timestamp;
      let formattedDate = "Unknown";
      let formattedTime = "Unknown";
      
      if (timeValue) {
        try {
          const date = new Date(timeValue);
          formattedDate = `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1).toString().padStart(2, "0")}/${date.getFullYear()}`;
          formattedTime = `${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
        } catch (error) {
          console.error("Error parsing date:", timeValue, error);
        }
      }

      const managerReport = {
        site_from: latestRecord.Train_Site_From || siteName || "Unknown Site",
        destination: latestRecord.Destination || "Unknown Destination",
        total_weight: totalWeight.toFixed(2),
        date: formattedDate,
        time: formattedTime,
        no_wagons: noWagons,
        wagons: wagons,
        customer_order_no: latestRecord.Customer_Order_No || "",
        brett_reference_no: latestRecord.Brett_Reference_No || "",
        flow_no: latestRecord.Flow_No || "",
        train_load_id: trainLoadId,
        PL_Check_Q1: latestRecord.PL_Check_Q1,
        PL_Check_Q2: latestRecord.PL_Check_Q2,
        PL_Check_Q3: latestRecord.PL_Check_Q3,
        PL_Check_Q4: latestRecord.PL_Check_Q4,
      };

      console.log("Manager report generated:", managerReport);
      setManagerReportData(managerReport);
      return managerReport;
    } catch (error) {
      console.error("Error in fetchManagerReport:", error);
      alert(`Error generating report: ${error.message}`);
      return null;
    } finally {
      setReportLoading(false);
    }
  };

  const fetchCustomerReport = async (trainLoadId) => {
    try {
      setReportLoading(true);
      const fullReport = await fetchFullReport(trainLoadId);
      
      if (!fullReport || !Array.isArray(fullReport) || fullReport.length === 0) {
        console.error("No full report data found");
        return null;
      }

      const latestRecord = fullReport[fullReport.length - 1];
      const noWagons = parseInt(latestRecord.No_Wagons) || 0;
      const wagons = [];
      let totalWeight = 0;

      for (let i = 1; i <= noWagons; i++) {
        const wagonId = latestRecord[`Wagon_ID_${i}`];
        if (wagonId && wagonId !== "N/A" && wagonId !== "0") {
          const materialCode = latestRecord[`Wagon_${i}_Material`];
          const materialName = getMaterialName(materialCode);
          const loadedTonnage = latestRecord[`Wagon_${i}_Loaded_Tonnage`];
          const comments = latestRecord[`Wagon_${i}_Comments`];

          wagons.push({
            wagon_no: i,
            wagon_id: wagonId,
            material: materialName,
            amount: loadedTonnage || "0",
            comments: comments || "",
          });
          
          totalWeight += parseFloat(loadedTonnage) || 0;
        }
      }

      const timeValue = latestRecord.time || latestRecord.timestamp;
      let formattedDate = "Unknown";
      if (timeValue) {
        try {
          const date = new Date(timeValue);
          formattedDate = `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1).toString().padStart(2, "0")}/${date.getFullYear()}`;
        } catch (error) {
          console.error("Error parsing date:", timeValue, error);
        }
      }

      const customerReport = {
        site_from: latestRecord.Train_Site_From || siteName,
        destination: latestRecord.Destination || "Unknown",
        total_weight: totalWeight.toFixed(2),
        date: formattedDate,
        no_wagons: noWagons,
        wagons: wagons,
        customer_order_no: latestRecord.Customer_Order_No || "",
        brett_reference_no: latestRecord.Brett_Reference_No || "",
        flow_no: latestRecord.Flow_No || "",
        train_load_id: trainLoadId,
      };

      setCustomerReportData(customerReport);
      return customerReport;
    } catch (error) {
      console.error("Error generating customer report:", error);
      return null;
    } finally {
      setReportLoading(false);
    }
  };

  const handleViewManagerReport = async (trainLoad) => {
    setLoadingButtons((prev) => ({ ...prev, [`manager-${trainLoad.id}`]: true }));
    setCurrentTrainLoad(trainLoad);
    setManagerReportDialogOpen(true);
    await fetchManagerReport(trainLoad.id);
    setLoadingButtons((prev) => ({ ...prev, [`manager-${trainLoad.id}`]: false }));
  };

  const handleViewCustomerReport = async (trainLoad) => {
    setLoadingButtons((prev) => ({ ...prev, [`customer-${trainLoad.id}`]: true }));
    setCurrentTrainLoad(trainLoad);
    setCustomerReportDialogOpen(true);
    await fetchCustomerReport(trainLoad.id);
    setLoadingButtons((prev) => ({ ...prev, [`customer-${trainLoad.id}`]: false }));
  };

  const handleGenerateCustomerPDF = async (trainLoad) => {
    setLoadingButtons((prev) => ({ ...prev, [`pdf-${trainLoad.id}`]: true }));
    await generateCustomerPDF(trainLoad);
    setLoadingButtons((prev) => ({ ...prev, [`pdf-${trainLoad.id}`]: false }));
  };

  const generateCustomerPDF = async (trainLoad) => {
    const customerReport = await fetchCustomerReport(trainLoad.id);
    if (!customerReport) return;

    const doc = new jsPDF();
    const logoToUse = companyLogo || logoBase64;
    const primaryColor = "#263d4f";
    const secondaryColor = "#5a6c7d";
    const borderColor = "#ccd6dd";
    const headerBgColor = "#e5edfe";
    const rowBgColor = "#f8fafe";

    doc.addImage(logoToUse, "PNG", 85, 15, 40, 20);
    doc.setFontSize(20);
    doc.setTextColor(primaryColor);
    doc.text("Train Loading Report", 105, 45, { align: "center" });

    const tableTop = 55;
    const tableWidth = 180;
    const tableLeft = 15;

    doc.setFillColor(headerBgColor);
    doc.rect(tableLeft, tableTop, tableWidth, 12, "F");
    doc.setFontSize(12);
    doc.setTextColor(primaryColor);
    doc.setFont(undefined, "bold");
    doc.text("Train Information", tableLeft + tableWidth / 2, tableTop + 8, { align: "center" });

    let yPos = tableTop + 12;

    doc.setFillColor(255);
    doc.rect(tableLeft, yPos, tableWidth / 2, 12, "F");
    doc.rect(tableLeft + tableWidth / 2, yPos, tableWidth / 2, 12, "F");

    doc.setFontSize(8);
    doc.setTextColor(secondaryColor);
    doc.setFont(undefined, "normal");
    doc.text("Date", tableLeft + 5, yPos + 5);
    doc.text("Flow Number", tableLeft + tableWidth / 2 + 5, yPos + 5);

    doc.setFontSize(10);
    doc.setTextColor(primaryColor);
    doc.setFont(undefined, "bold");
    doc.text(customerReport.date, tableLeft + 5, yPos + 10);
    doc.text(customerReport.flow_no || "N/A", tableLeft + tableWidth / 2 + 5, yPos + 10);

    yPos += 12;

    doc.setFillColor(rowBgColor);
    doc.rect(tableLeft, yPos, tableWidth / 2, 12, "F");
    doc.rect(tableLeft + tableWidth / 2, yPos, tableWidth / 2, 12, "F");

    doc.setFontSize(8);
    doc.setTextColor(secondaryColor);
    doc.setFont(undefined, "normal");
    doc.text("Customer Order No.", tableLeft + 5, yPos + 5);
    doc.text("Brett Reference No.", tableLeft + tableWidth / 2 + 5, yPos + 5);

    doc.setFontSize(10);
    doc.setTextColor(primaryColor);
    doc.setFont(undefined, "bold");
    doc.text(customerReport.customer_order_no || "N/A", tableLeft + 5, yPos + 10);
    doc.text(customerReport.brett_reference_no || "N/A", tableLeft + tableWidth / 2 + 5, yPos + 10);

    yPos += 12;

    doc.setFillColor(255);
    doc.rect(tableLeft, yPos, tableWidth / 2, 12, "F");
    doc.rect(tableLeft + tableWidth / 2, yPos, tableWidth / 2, 12, "F");

    doc.setFontSize(8);
    doc.setTextColor(secondaryColor);
    doc.setFont(undefined, "normal");
    doc.text("Destination", tableLeft + 5, yPos + 5);
    doc.text("Number of Wagons", tableLeft + tableWidth / 2 + 5, yPos + 5);

    doc.setFontSize(10);
    doc.setTextColor(primaryColor);
    doc.setFont(undefined, "bold");
    doc.text(customerReport.destination, tableLeft + 5, yPos + 10);
    doc.text(customerReport.no_wagons.toString(), tableLeft + tableWidth / 2 + 5, yPos + 10);

    yPos += 20;

    doc.setFillColor(headerBgColor);
    doc.rect(tableLeft, yPos, tableWidth, 12, "F");
    doc.setFontSize(12);
    doc.setTextColor(primaryColor);
    doc.setFont(undefined, "bold");
    doc.text("Wagon Information", tableLeft + tableWidth / 2, yPos + 8, { align: "center" });

    yPos += 12;

    doc.setFillColor(headerBgColor);
    doc.rect(tableLeft, yPos, tableWidth, 8, "F");

    const colWidths = [tableWidth * 0.2, tableWidth * 0.4, tableWidth * 0.2, tableWidth * 0.2];
    const colPositions = [
      tableLeft,
      tableLeft + colWidths[0],
      tableLeft + colWidths[0] + colWidths[1],
      tableLeft + colWidths[0] + colWidths[1] + colWidths[2],
    ];

    doc.setFontSize(9);
    doc.setTextColor(primaryColor);
    doc.setFont(undefined, "bold");
    doc.text("Wagon ID", colPositions[0] + colWidths[0] / 2, yPos + 5, { align: "center" });
    doc.text("Material", colPositions[1] + colWidths[1] / 2, yPos + 5, { align: "center" });
    doc.text("Loaded Weight", colPositions[2] + colWidths[2] / 2, yPos + 5, { align: "center" });
    doc.text("Comments", colPositions[3] + colWidths[3] / 2, yPos + 5, { align: "center" });

    yPos += 8;

    customerReport.wagons.forEach((wagon, index) => {
      const bgColor = index % 2 === 0 ? 255 : rowBgColor;
      doc.setFillColor(bgColor);
      doc.rect(tableLeft, yPos, tableWidth, 8, "F");

      doc.setFontSize(8);
      doc.setTextColor(primaryColor);
      doc.setFont(undefined, "normal");
      doc.text(wagon.wagon_id, colPositions[0] + colWidths[0] / 2, yPos + 5, { align: "center" });
      doc.text(wagon.material, colPositions[1] + colWidths[1] / 2, yPos + 5, { align: "center" });
      doc.text(`${wagon.amount} t`, colPositions[2] + colWidths[2] / 2, yPos + 5, { align: "center" });
      doc.text(wagon.comments, colPositions[3] + colWidths[3] / 2, yPos + 5, { align: "center" });
      yPos += 8;

      if (yPos > 270 && index < customerReport.wagons.length - 1) {
        doc.addPage();
        yPos = 20;
        doc.setFillColor(headerBgColor);
        doc.rect(tableLeft, yPos, tableWidth, 8, "F");
        doc.setFontSize(9);
        doc.setTextColor(primaryColor);
        doc.setFont(undefined, "bold");
        doc.text("Wagon ID", colPositions[0] + colWidths[0] / 2, yPos + 5, { align: "center" });
        doc.text("Material", colPositions[1] + colWidths[1] / 2, yPos + 5, { align: "center" });
        doc.text("Loaded Weight", colPositions[2] + colWidths[2] / 2, yPos + 5, { align: "center" });
        doc.text("Comments", colPositions[3] + colWidths[3] / 2, yPos + 5, { align: "center" });
        yPos += 8;
      }
    });

    doc.setFillColor(headerBgColor);
    doc.rect(tableLeft, yPos, tableWidth, 8, "F");
    doc.setFontSize(9);
    doc.setTextColor(primaryColor);
    doc.setFont(undefined, "bold");
    doc.text("Total Weight", colPositions[0] + colWidths[0] / 2, yPos + 5, { align: "center" });
    doc.text("", colPositions[1] + colWidths[1] / 2, yPos + 5, { align: "center" });
    doc.text(`${customerReport.total_weight} t`, colPositions[2] + colWidths[2] / 2, yPos + 5, { align: "center" });
    doc.text("", colPositions[3] + colWidths[3] / 2, yPos + 5, { align: "center" });

    const pageHeight = doc.internal.pageSize.height;
    doc.setFontSize(8);
    doc.setTextColor(secondaryColor);
    doc.setFont(undefined, "normal");
    doc.text(
      `© ${new Date().getFullYear()} Batching & Blending Systems Ltd. All rights reserved. | Generated on ${new Date().toLocaleString()}`,
      105,
      pageHeight - 10,
      { align: "center" }
    );

    doc.save(`train_loading_report_${customerReport.customer_order_no || trainLoad.id}.pdf`);
  };

  const handleDialogClose = () => setDialogOpen(false);

  const handleSubmit = () => {
    setTrainLoads([]);
    setLoading(true);
    setPageLoading(true);
    fetchTrainLoads().finally(() => {
      setLoading(false);
    });
    handleDialogClose();
  };

  const displayedRows = trainLoads.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const totalPages = Math.ceil(trainLoads.length / rowsPerPage);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <Card>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" p={2}>
            <VuiTypography variant="lg" color="white">
              Train Load Export - {siteName}
            </VuiTypography>
            <div>
              <VuiButton
                variant="contained"
                color="secondary"
                onClick={() => setDialogOpen(true)}
                sx={{ mr: 2 }}
              >
                Edit Date Range
              </VuiButton>
            </div>
          </VuiBox>

          {loading ? (
            <VuiBox display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </VuiBox>
          ) : pageLoading ? (
            <VuiBox display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </VuiBox>
          ) : trainLoads.length === 0 ? (
            <VuiBox display="flex" justifyContent="center" py={4}>
              <VuiTypography variant="h6" color="white">
                No complete train loads found for the selected date range.
              </VuiTypography>
            </VuiBox>
          ) : (
            <>
              <Table
                columns={columns}
                rows={displayedRows.map((row) => ({
                  Destination: row.destination,
                  "Customer Order No": row.customerOrderNo,
                  "Brett Reference No": row.brettReferenceNo,
                  Actions: (
                    <div>
                      <Tooltip title="View Manager Report">
                        <IconButton
                          color="primary"
                          onClick={() => handleViewManagerReport(row)}
                          disabled={loadingButtons[`manager-${row.id}`]}
                        >
                          {loadingButtons[`manager-${row.id}`] ? (
                            <CircularProgress size={24} />
                          ) : (
                            <Icon>visibility</Icon>
                          )}
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="View Customer Report">
                        <IconButton
                          color="info"
                          onClick={() => handleViewCustomerReport(row)}
                          disabled={loadingButtons[`customer-${row.id}`]}
                        >
                          {loadingButtons[`customer-${row.id}`] ? (
                            <CircularProgress size={24} />
                          ) : (
                            <Icon>description</Icon>
                          )}
                        </IconButton>
                      </Tooltip>
                    </div>
                  ),
                }))}
              />

              <VuiBox display="flex" justifyContent="center" p={2}>
                <VuiPagination>
                  <VuiPagination
                    item
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                  >
                    <Icon>keyboard_arrow_left</Icon>
                  </VuiPagination>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <VuiPagination
                      key={i + 1}
                      item
                      active={page === i + 1}
                      onClick={() => setPage(i + 1)}
                    >
                      {i + 1}
                    </VuiPagination>
                  ))}
                  <VuiPagination
                    item
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                  >
                    <Icon>keyboard_arrow_right</Icon>
                  </VuiPagination>
                </VuiPagination>
              </VuiBox>
            </>
          )}
        </Card>
      </VuiBox>

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>Select Date Range for Train Loads</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3} mt={1}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Start Date & Time"
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={dateRange.startDate}
                onChange={(e) => setDateRange((prev) => ({ ...prev, startDate: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="End Date & Time"
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={dateRange.endDate}
                onChange={(e) => setDateRange((prev) => ({ ...prev, endDate: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            disabled={!dateRange.startDate || !dateRange.endDate}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manager Report Dialog */}
      <Dialog
        open={managerReportDialogOpen}
        onClose={() => setManagerReportDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#1E1E2F",
            color: "white",
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle>
          <VuiTypography variant="lg" color="white">
            Train Load Report (Manager)
          </VuiTypography>
        </DialogTitle>
        <DialogContent dividers>
          {reportLoading ? (
            <VuiBox display="flex" justifyContent="center" py={4}>
              <CircularProgress color="inherit" />
            </VuiBox>
          ) : managerReportData ? (
            <VuiBox>
              <VuiBox mb={4}>
                <VuiBox display="grid" gridTemplateColumns="repeat(auto-fill, minmax(240px, 1fr))" gap={2}>
                  <VuiTypography variant="body2" color="white">
                    <strong>Date:</strong> {managerReportData.date || "Unknown"}
                  </VuiTypography>
                  <VuiTypography variant="body2" color="white">
                    <strong>Time:</strong> {managerReportData.time || "Unknown"}
                  </VuiTypography>
                  <VuiTypography variant="body2" color="white">
                    <strong>From:</strong> {managerReportData.site_from}
                  </VuiTypography>
                  <VuiTypography variant="body2" color="white">
                    <strong>Destination:</strong> {managerReportData.destination}
                  </VuiTypography>
                  <VuiTypography variant="body2" color="white">
                    <strong>Total Weight:</strong> {managerReportData.total_weight} T
                  </VuiTypography>
                  <VuiTypography variant="body2" color="white">
                    <strong>No. of Wagons:</strong> {managerReportData.no_wagons}
                  </VuiTypography>
                  <VuiTypography variant="body2" color="white">
                    <strong>Customer Order No:</strong> {managerReportData.customer_order_no || "N/A"}
                  </VuiTypography>
                  <VuiTypography variant="body2" color="white">
                    <strong>Brett Reference No:</strong> {managerReportData.brett_reference_no || "N/A"}
                  </VuiTypography>
                  <VuiTypography variant="body2" color="white">
                    <strong>Flow No:</strong> {managerReportData.flow_no || "N/A"}
                  </VuiTypography>
                </VuiBox>
              </VuiBox>

              <VuiTypography variant="h6" color="white" mb={2}>
                Wagon Details
              </VuiTypography>
              <VuiBox
                component="table"
                width="100%"
                sx={{
                  borderCollapse: "collapse",
                  fontSize: "0.75rem",
                  "& th, & td": {
                    border: "1px solid rgba(255, 255, 255, 0.12)",
                    padding: "6px 8px",
                    textAlign: "center",
                  },
                  "& th": {
                    backgroundColor: "#2D3748",
                    color: "#FFFFFF",
                    fontWeight: "bold",
                  },
                  "& td": {
                    backgroundColor: "#1A202C",
                    color: "#E2E8F0",
                  },
                  "& tbody tr:nth-of-type(even) td": {
                    backgroundColor: "#2A2F45",
                  },
                }}
              >
                <thead>
                  <tr>
                    <th>Wagon No</th>
                    <th>Wagon ID</th>
                    <th>Material</th>
                    <th>Target Weight (T)</th>
                    <th>Loaded Weight (T)</th>
                    <th>Average Moisture (%)</th>
                    <th>Comments</th>
                  </tr>
                </thead>
                <tbody>
                  {managerReportData.wagons.map((wagon, index) => (
                    <tr key={index}>
                      <td>{wagon.wagon_no}</td>
                      <td>{wagon.wagon_id}</td>
                      <td>{wagon.material}</td>
                      <td>{wagon.target_tonnage}</td>
                      <td>{wagon.amount}</td>
                      <td>{wagon.moisture}</td>
                      <td>{wagon.comments}</td>
                    </tr>
                  ))}
                </tbody>
              </VuiBox>

              <VuiBox mt={4}>
                <VuiTypography variant="h6" color="white" mb={2}>
                  Pre-Loading Checks
                </VuiTypography>
                <VuiBox
                  component="table"
                  width="100%"
                  sx={{
                    borderCollapse: "collapse",
                    fontSize: "0.9rem",
                    "& th, & td": {
                      border: "1px solid rgba(255, 255, 255, 0.12)",
                      padding: "12px 16px",
                      textAlign: "left",
                    },
                    "& th": {
                      backgroundColor: "#2D3748",
                      color: "#FFFFFF",
                      fontWeight: "bold",
                      width: "70%",
                    },
                    "& td": {
                      backgroundColor: "#1A202C",
                      color: "#E2E8F0",
                      width: "30%",
                    },
                  }}
                >
                  <thead>
                    <tr>
                      <th>Check Description</th>
                      <th>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>All wagons have been correctly and evenly loaded across the length of every wagon</td>
                      <td style={{ color: formatCheckValue(managerReportData.PL_Check_Q1) === "Yes" ? "#4CAF50" : "#F44336", fontWeight: "bold" }}>
                        {formatCheckValue(managerReportData.PL_Check_Q1)}
                      </td>
                    </tr>
                    <tr>
                      <td>Any spilt product is cleaned from the wagon sides, raves, buffers, couplings and air pipes</td>
                      <td style={{ color: formatCheckValue(managerReportData.PL_Check_Q2) === "Yes" ? "#4CAF50" : "#F44336", fontWeight: "bold" }}>
                        {formatCheckValue(managerReportData.PL_Check_Q2)}
                      </td>
                    </tr>
                    <tr>
                      <td>Check that the wagon is not leaking product from the doors/drain holes in wagon</td>
                      <td style={{ color: formatCheckValue(managerReportData.PL_Check_Q3) === "Yes" ? "#4CAF50" : "#F44336", fontWeight: "bold" }}>
                        {formatCheckValue(managerReportData.PL_Check_Q3)}
                      </td>
                    </tr>
                    <tr>
                      <td>Track is cleared from spillage</td>
                      <td style={{ color: formatCheckValue(managerReportData.PL_Check_Q4) === "Yes" ? "#4CAF50" : "#F44336", fontWeight: "bold" }}>
                        {formatCheckValue(managerReportData.PL_Check_Q4)}
                      </td>
                    </tr>
                  </tbody>
                </VuiBox>
              </VuiBox>
            </VuiBox>
          ) : (
            <VuiTypography variant="body2" color="text">
              No report data available
            </VuiTypography>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2 }}>
          <VuiButton variant="outlined" color="secondary" onClick={() => setManagerReportDialogOpen(false)}>
            Close
          </VuiButton>
          {managerReportData && (
            <CSVLink
              data={[
                ["Batching & Blending Systems Ltd."],
                ["Train Loading Report"],
                ["Report Type: Manager"],
                [`Generated: ${new Date().toLocaleString()}`],
                [],
                ["Reference Information"],
                [
                  "Flow Number:",
                  managerReportData.flow_no || "",
                  "Customer Order No:",
                  managerReportData.customer_order_no || "",
                  "Brett Reference No:",
                  managerReportData.brett_reference_no || "",
                ],
                [],
                ["Summary Information"],
                [
                  "From:",
                  managerReportData.site_from || "Unknown Site",
                  "Destination:",
                  managerReportData.destination || "Unknown Destination",
                ],
                ["Date:", managerReportData.date || "Unknown", "Time:", managerReportData.time || "Unknown"],
                ["Total Weight (T):", managerReportData.total_weight || "0"],
                ["Number of Wagons:", managerReportData.no_wagons || "0"],
                [],
                ["Wagon Details"],
                ["Wagon ID", "Material", "Target Weight (T)", "Loaded Weight (T)", "Average Moisture (%)", "Comments"],
                ...managerReportData.wagons.map((wagon) => [
                  wagon.wagon_id,
                  wagon.material,
                  wagon.target_tonnage,
                  wagon.amount,
                  wagon.moisture,
                  wagon.comments,
                ]),
                [],
                ["Pre-Loading Checks"],
                ["Check Description", "Result"],
                [
                  "All wagons have been correctly and evenly loaded across the length of every wagon",
                  formatCheckValue(managerReportData.PL_Check_Q1),
                ],
                [
                  "Any split product is cleaned from the wagon sides, raves, buffers, couplings and air pipes",
                  formatCheckValue(managerReportData.PL_Check_Q2),
                ],
                [
                  "Check that the wagon is not leaking product from the doors/drain holes in wagon",
                  formatCheckValue(managerReportData.PL_Check_Q3),
                ],
                ["Track is cleared from spillage", formatCheckValue(managerReportData.PL_Check_Q4)],
                [],
                ["Total Weight (T):", managerReportData.total_weight || "0"],
              ]}
              filename={`train_load_${currentTrainLoad?.id}_manager_report.csv`}
              style={{ textDecoration: "none", marginRight: "8px" }}
            >
              <VuiButton variant="contained" color="primary" sx={{ mr: 1 }}>
                Download CSV
              </VuiButton>
            </CSVLink>
          )}
        </DialogActions>
      </Dialog>

      {/* Customer Report Dialog */}
      <Dialog
        open={customerReportDialogOpen}
        onClose={() => setCustomerReportDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#ffffff",
            color: "#1f2937",
            borderRadius: 2,
            fontFamily: "Arial, sans-serif",
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
          <VuiBox display="flex" justifyContent="center" alignItems="center" flexDirection="column">
            {companyLogo && (
              <img
                src={companyLogo}
                alt="Company Logo"
                style={{ maxWidth: "240px", maxHeight: "120px", marginBottom: "10px" }}
              />
            )}
            <VuiTypography variant="h4" color="#263d4f" fontWeight="bold">
              Train Loading Report
            </VuiTypography>
          </VuiBox>
        </DialogTitle>
        <DialogContent dividers>
          {reportLoading ? (
            <VuiBox display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </VuiBox>
          ) : customerReportData ? (
            <VuiBox>
              <VuiBox
                component="table"
                width="90%"
                mx="auto"
                sx={{
                  border: "2px solid #ccd6dd",
                  borderCollapse: "collapse",
                  borderRadius: "8px",
                  overflow: "hidden",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
              >
                <tbody>
                  <tr>
                    <td colSpan={2} style={{ backgroundColor: "#e5edfe", textAlign: "center", padding: "12px", fontWeight: "bold", fontSize: "15px", borderBottom: "2px solid #ccd6dd", color: "#263d4f" }}>
                      Train Information
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: "12px", borderRight: "1px solid #e8ecf0", borderBottom: "1px solid #e8ecf0", backgroundColor: "#fff" }}>
                      <div style={{ fontSize: "11px", color: "#5a6c7d" }}>Date</div>
                      <div style={{ fontSize: "14px", fontWeight: "bold", color: "#263d4f" }}>{customerReportData.date}</div>
                    </td>
                    <td style={{ padding: "12px", borderBottom: "1px solid #e8ecf0", backgroundColor: "#fff" }}>
                      <div style={{ fontSize: "11px", color: "#5a6c7d" }}>Flow Number</div>
                      <div style={{ fontSize: "14px", fontWeight: "bold", color: "#263d4f" }}>{customerReportData.flow_no || "N/A"}</div>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: "12px", borderRight: "1px solid #e8ecf0", borderBottom: "1px solid #e8ecf0", backgroundColor: "#fff" }}>
                      <div style={{ fontSize: "11px", color: "#5a6c7d" }}>Customer Order Number</div>
                      <div style={{ fontSize: "14px", fontWeight: "bold", color: "#263d4f" }}>{customerReportData.customer_order_no || "N/A"}</div>
                    </td>
                    <td style={{ padding: "12px", borderBottom: "1px solid #e8ecf0", backgroundColor: "#fff" }}>
                      <div style={{ fontSize: "11px", color: "#5a6c7d" }}>Brett Reference Number</div>
                      <div style={{ fontSize: "14px", fontWeight: "bold", color: "#263d4f" }}>{customerReportData.brett_reference_no || "N/A"}</div>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: "12px", borderRight: "1px solid #e8ecf0", backgroundColor: "#fff" }}>
                      <div style={{ fontSize: "11px", color: "#5a6c7d" }}>Destination</div>
                      <div style={{ fontSize: "14px", fontWeight: "bold", color: "#263d4f" }}>{customerReportData.destination}</div>
                    </td>
                    <td style={{ padding: "12px", backgroundColor: "#fff" }}>
                      <div style={{ fontSize: "11px", color: "#5a6c7d" }}>Number of Wagons</div>
                      <div style={{ fontSize: "14px", fontWeight: "bold", color: "#263d4f" }}>{customerReportData.no_wagons}</div>
                    </td>
                  </tr>
                </tbody>
              </VuiBox>

              <VuiBox
                component="table"
                width="90%"
                mx="auto"
                sx={{
                  border: "2px solid #ccd6dd",
                  borderCollapse: "collapse",
                  marginTop: "20px",
                  borderRadius: "8px",
                  overflow: "hidden",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
              >
                <tbody>
                  <tr>
                    <td colSpan={4} style={{ backgroundColor: "#e5edfe", textAlign: "center", padding: "12px", fontWeight: "bold", fontSize: "15px", borderBottom: "2px solid #ccd6dd", color: "#263d4f" }}>
                      Wagon Information
                    </td>
                  </tr>
                  <tr style={{ backgroundColor: "#e5edfe", color: "#263d4f" }}>
                    <td style={{ border: "1px solid #ccd6dd", padding: "8px", textAlign: "center", fontSize: "11px" }}>Wagon ID</td>
                    <td style={{ border: "1px solid #ccd6dd", padding: "8px", textAlign: "center", fontSize: "11px" }}>Material</td>
                    <td style={{ border: "1px solid #ccd6dd", padding: "8px", textAlign: "center", fontSize: "11px" }}>Loaded Weight</td>
                    <td style={{ border: "1px solid #ccd6dd", padding: "8px", textAlign: "center", fontSize: "11px" }}>Comments</td>
                  </tr>
                  {customerReportData.wagons.map((wagon, index) => (
                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? "#fff" : "#f8fafe" }}>
                      <td style={{ border: "1px solid #ccd6dd", padding: "10px", textAlign: "center" }}>
                        <span style={{ fontSize: "14px", fontWeight: "bold", color: "#263d4f" }}>{wagon.wagon_id}</span>
                      </td>
                      <td style={{ border: "1px solid #ccd6dd", padding: "10px", textAlign: "center" }}>
                        <span style={{ fontSize: "14px", fontWeight: "bold", color: "#263d4f" }}>{wagon.material}</span>
                      </td>
                      <td style={{ border: "1px solid #ccd6dd", padding: "10px", textAlign: "center" }}>
                        <span style={{ fontSize: "14px", fontWeight: "bold", color: "#263d4f" }}>{wagon.amount} t</span>
                      </td>
                      <td style={{ border: "1px solid #ccd6dd", padding: "10px", textAlign: "center" }}>
                        <span style={{ fontSize: "14px", fontWeight: "bold", color: "#263d4f" }}>{wagon.comments}</span>
                      </td>
                    </tr>
                  ))}
                  <tr style={{ backgroundColor: "#e5edfe" }}>
                    <td style={{ border: "1px solid #ccd6dd", padding: "10px", textAlign: "center", fontSize: "11px" }}>Total Weight</td>
                    <td style={{ border: "1px solid #ccd6dd" }}></td>
                    <td style={{ border: "1px solid #ccd6dd", padding: "10px", textAlign: "center" }}>
                      <span style={{ fontSize: "14px", fontWeight: "bold", color: "#263d4f" }}>{customerReportData.total_weight} t</span>
                    </td>
                    <td style={{ border: "1px solid #ccd6dd" }}></td>
                  </tr>
                </tbody>
              </VuiBox>

              <VuiBox textAlign="center" mt={3} mb={1}>
                <VuiTypography variant="caption" color="#5a6c7d">
                  © {new Date().getFullYear()} Batching & Blending Systems Ltd. All rights reserved. | Generated on {new Date().toLocaleString()}
                </VuiTypography>
              </VuiBox>
            </VuiBox>
          ) : (
            <VuiTypography variant="body2" color="text">
              No report data available
            </VuiTypography>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2 }}>
          <VuiButton variant="outlined" color="secondary" onClick={() => setCustomerReportDialogOpen(false)}>
            Close
          </VuiButton>
          {customerReportData && (
            <VuiButton variant="contained" color="primary" onClick={() => handleGenerateCustomerPDF(currentTrainLoad)}>
              Download PDF
            </VuiButton>
          )}
        </DialogActions>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}

export default TrainLoadDataExport;